import { useEffect, useState } from 'react';
import { keepPreviousData, useQuery } from '@tanstack/react-query';
import { useConnection, usePublicClient } from 'wagmi';
import type { TxRequest } from '../components/tx/useTx.ts';

/** A stable string for a request, so two renders with equal arguments share one estimate. */
export function requestKey(req: TxRequest | null): string | null {
  return req
    ? JSON.stringify({ a: req.address, f: req.functionName, g: req.args ?? [], v: req.value ?? 0n }, (_k, v: unknown) => (typeof v === 'bigint' ? v.toString() : v))
    : null;
}

/**
 * What a transaction will cost the wallet in BNB before it is sent: the node's gas estimate × its gas price. `null`
 * while unknown and whenever the call would revert as things stand (nothing to approve yet, wrong amount…), so the
 * line under the button only ever shows a fee that can actually be paid.
 * Deep referral trees make a stake or withdrawal settle every ancestor (≈ 10 M gas at 128 levels); this is where a
 * member learns that before the wallet opens.
 */
export function useFee(req: TxRequest | null, enabled = true): bigint | null {
  const client = usePublicClient();
  const { address } = useConnection();
  const key = requestKey(req);
  const settled = useDebounced(key, 300); // the amount field changes the request on every keystroke
  const q = useQuery({
    queryKey: ['fee', address, settled],
    enabled: Boolean(client && address && req && enabled && settled === key),
    staleTime: 60_000,
    retry: false,
    placeholderData: keepPreviousData,
    queryFn: async () => {
      const [gas, gasPrice] = await Promise.all([client!.estimateContractGas({ ...req!, account: address! } as never), client!.getGasPrice()]);
      return gas * gasPrice;
    },
  });
  return enabled && req && q.data !== undefined ? q.data : null;
}

function useDebounced<T>(value: T, ms: number): T {
  const [v, setV] = useState(value);
  useEffect(() => {
    if (v === value) return;
    const id = setTimeout(() => setV(value), ms);
    return () => clearTimeout(id);
  }, [value, v, ms]);
  return v;
}
